<script lang="ts" setup>
import { isContentArray } from '~/utils/helpers'
import type { ExtendedAccordionItem, LayoutButton } from '~/types/elementsUi'
import type { RouteLocationRaw } from '#vue-router'
import type { TailwindColor } from '#shared/types/elementsUi'

interface FAQSectionProps {
  baseKey?: string
  title?: string
  questions?: string
  backgroundColor?: TailwindColor
  button?: LayoutButton
  hideButton?: boolean
}

const props = withDefaults(defineProps<FAQSectionProps>(), {
  questions: 'components.faq.questions',
  title: 'components.faq.title',
  backgroundColor: undefined,
  button: () => ({
    url: 'vragen',
    label: 'buttons.show-more',
    type: 'internal',
    color: 'transparent'
  })
})

const { tm } = useI18n(props.baseKey)

const route = useRoute()

const frequentQuestions = computed<ExtendedAccordionItem[]>(() => tm(props.questions))

onMounted(() => {
  const parsedHash = route.hash.substring(1)
  const isOwnQuestion = frequentQuestions.value.some(
    (question) => question.id_DNT === parsedHash
  )

  if (parsedHash && isOwnQuestion) {
    scrollIntoView(parsedHash)
  }
})
</script>

<template>
  <VSection :title :ui="{ section: backgroundColor && `bg-${backgroundColor}` }">
    <template #content>
      <VAccordion :items="frequentQuestions" type="multiple" :unmount-on-hide="false">
        <template #default="{ item: question }">
          <h3 :id="question.id_DNT" class="paragraph-title mt-0 text-xl">
            {{ question.label }}
          </h3>
        </template>

        <template #content="{ item: question }">
          <div class="prose max-w-section">
            <template v-if="question.answer && isContentArray(question.answer)">
              <div
                v-for="(answer, answerIndex) in question.answer"
                :key="`answer-${answerIndex}`"
              >
                <strong v-if="answer.label">{{ answer.label }}</strong>
                <VRenderMarkdown
                  :content="
                    Array.isArray(answer.answer)
                      ? answer.answer.join('\n')
                      : answer.answer
                  "
                  class="*:first:mt-0"
                  :class="[answerIndex === question.answer.length - 1 && '*:last:mb-4']"
                />
              </div>
            </template>

            <VRenderMarkdown
              v-else
              :content="
                Array.isArray(question.answer)
                  ? question.answer.join('\n\n')
                  : question.answer
              "
              class="*:first:mt-0 *:last:mb-4"
            />

            <div v-if="question.button" class="section-button mb-4">
              <VButton :to="{ name: question.button.url } as RouteLocationRaw">
                {{ question.button.label }}
              </VButton>
            </div>
          </div>
        </template>
      </VAccordion>

      <VButton
        v-if="!hideButton && button"
        :to="{ name: button.url } as RouteLocationRaw"
        :color="button.color"
        icon-right="fa-arrow-right"
        width="full"
        class="mt-6"
        :label="button.label"
        :disabled="button.disabled"
        @click="button.click && button.click()"
      />
    </template>
  </VSection>
</template>

<style scoped></style>
